import { api, ApiError } from '../api.js';
import { ce, el, clear, avatarEl, type TextFieldEl } from '../dom.js';
import { t } from '../i18n.js';
import { icons } from '../icons.js';
import { sheet, type Sheet } from '../components/sheet.js';

interface TodayMember {
  id: number;
  name: string;
  avatar: string;
  color: string;
  hasPin: boolean;
  doneToday: number;
  minutesToday: number;
}

interface TodayTask {
  id: number;
  title: string;
  status: 'open' | 'done';
  minutes: number;
  eligibleMemberIds: number[];
  completedBy: number | null;
}

interface TodayState {
  date: string;
  /** Who is looking (ADR-0058) — used to find "your" column. Null on a
   *  shared display, which has no viewer (ADR-0061). */
  viewerId: number | null;
  members: TodayMember[];
  tasks: TodayTask[];
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * How long "acting as <member>" survives without interaction on a shared
 * display before the board snaps back to nobody-selected (ADR-0061,
 * human-confirmed 60s). Any tap or key resets it — one member can clear
 * several chores — but a walk-away never leaves the next person holding
 * someone else's identity.
 */
const ACTING_IDLE_MS = 60_000;

/**
 * Tally marks (ADR-0033): one ink stroke per thing done today, groups of
 * five closed with the terracotta cross-stroke — the app's own icon as a
 * wordless "look who's been busy". Capped so a heroic day can't break the
 * layout.
 */
function tallyMarks(count: number): SVGSVGElement {
  const capped = Math.min(count, 25);
  const groups = Math.max(1, Math.ceil(capped / 5));
  const groupW = 20;
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'tally');
  svg.setAttribute('viewBox', `0 0 ${groups * groupW} 20`);
  svg.setAttribute('width', String(groups * groupW));
  svg.setAttribute('height', '20');
  svg.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < capped; i++) {
    const g = Math.floor(i / 5);
    const pos = i % 5;
    const line = document.createElementNS(SVG_NS, 'line');
    if (pos < 4) {
      const x = g * groupW + pos * 4 + 2;
      line.setAttribute('x1', String(x));
      line.setAttribute('y1', '3');
      line.setAttribute('x2', String(x));
      line.setAttribute('y2', '17');
    } else {
      line.setAttribute('x1', String(g * groupW));
      line.setAttribute('y1', '15');
      line.setAttribute('x2', String(g * groupW + 15));
      line.setAttribute('y2', '5');
      line.setAttribute('class', 'cross');
    }
    line.setAttribute('stroke-width', '2');
    line.setAttribute('stroke-linecap', 'round');
    svg.append(line);
  }
  return svg;
}

/** Contiguous runs of column indexes, so a chip can span each run. */
function runsOf(cols: number[]): [number, number][] {
  const sorted = [...cols].sort((a, b) => a - b);
  const runs: [number, number][] = [];
  for (const c of sorted) {
    const last = runs[runs.length - 1];
    if (last && c === last[1] + 1) last[1] = c;
    else runs.push([c, c]);
  }
  return runs;
}

/** What a tappable chip does. `null` means the chip is inert text. */
interface ChipAction {
  /** Accessible name — what the tap DOES; the chip still shows the title. */
  label: string;
  run: (button: HTMLButtonElement) => void;
  /** Marks the chip in the DOM for specs: whose tap this is. */
  data: Record<string, string>;
}

export interface TodayOptions {
  /**
   * Shared-display session (ADR-0049 sentinel cookie). Turns the header row
   * into a member switcher and chips into complete-on-behalf buttons
   * (ADR-0061). Off — and therefore ADR-0058's behaviour exactly — on a
   * normal member session.
   */
  display?: boolean;
  /**
   * Fired after this board closes a task, so a sibling surface showing the
   * same tasks (the shared display's ADR-0049 list) can refresh instead of
   * going stale.
   */
  onChanged?: () => void;
  /**
   * Hands the caller this board's reload, for the other direction: the
   * display's list completes a task, the board must catch up.
   */
  bindReload?: (reload: () => void) => void;
}

// The Today board (ADR-0033): member columns, task chips spanning the
// columns of everyone eligible, done-today chips in the doer's column.
// The nudge is visual only — tally marks and a faint column tint; no
// numbers, no ranking, no words.
//
// Two action modes, and they never overlap:
//
// * **Member session (ADR-0058).** A chip that covers YOUR column is a
//   button that completes the task and credits you. It posts to
//   `/api/tasks/:id/complete` with NO body — the ordinary self-complete
//   path, which re-derives the actor from the session and re-checks
//   eligibility, so the client never asserts who earns. Chips over other
//   people's columns stay inert text, including for admins. There is no
//   member switcher here.
//
// * **Shared display (ADR-0061).** There is no viewer, so nothing is
//   "yours". Tap a member in the header row to act as them (entering their
//   PIN if they have one — ADR-0049's rule, kept); their chips then become
//   buttons that complete with `{ asMemberId, pin? }` — the on-behalf
//   branch that already exists, unwidened. The selection is visible the
//   whole time, can be exited deliberately, and expires after 60s idle.
export function renderToday(options: TodayOptions = {}): HTMLElement {
  const display = options.display === true;

  const root = el('div', {});
  const error = el('p', { class: 'error' });
  const grid = el('div', { class: 'today-grid', id: 'today-grid' });
  const empty = el('p', { class: 'muted' }, t('todayEmpty'));
  empty.style.display = 'none';

  /** The last board the server gave us, so selecting a member repaints
   *  instantly instead of waiting on a round trip. */
  let current: TodayState | null = null;

  // --- Shared-display "acting as" mode (ADR-0061) -------------------------

  /** The member whose chips are currently tappable, display mode only. */
  let acting: TodayMember | null = null;
  /**
   * Their PIN, held only while the mode is on and dropped the moment it
   * ends. The completion route is the only thing that can check a PIN, so a
   * wrong one surfaces on the first tap (see `completeAs`) rather than at
   * entry — deliberately, because verifying it earlier would mean a new
   * PIN-checking endpoint, i.e. a brute-force oracle this app does not have.
   */
  let actingPin: string | undefined;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  let idleTick: ReturnType<typeof setInterval> | undefined;
  let idleUntil = 0;

  const idleBar = el('span', { class: 'today-acting-bar-fill' });
  const banner = el('div', { class: 'today-acting', id: 'today-acting' });
  banner.hidden = true;

  function paintIdleBar(): void {
    const left = Math.max(0, idleUntil - Date.now());
    idleBar.style.width = `${Math.round((left / ACTING_IDLE_MS) * 100)}%`;
  }

  function clearIdle(): void {
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    if (idleTick !== undefined) clearInterval(idleTick);
    idleTimer = undefined;
    idleTick = undefined;
  }

  /** Any interaction buys another full window. */
  function resetIdle(): void {
    if (!acting) return;
    clearIdle();
    idleUntil = Date.now() + ACTING_IDLE_MS;
    paintIdleBar();
    idleTimer = setTimeout(() => stopActing(), ACTING_IDLE_MS);
    idleTick = setInterval(paintIdleBar, 1000);
  }

  function paintBanner(): void {
    clear(banner);
    if (!acting) {
      banner.hidden = true;
      return;
    }
    banner.hidden = false;
    const exit = el(
      'button',
      {
        class: 'today-acting-exit',
        type: 'button',
        id: 'today-acting-exit',
        onclick: () => stopActing(),
      },
      t('todayActingExit'),
    );
    banner.append(
      avatarEl(acting.name, acting.avatar, acting.color, 32),
      el('span', { class: 'today-acting-name' }, t('todayActingAs', { name: acting.name })),
      // The countdown is a shrinking bar, not a number: it shows the mode is
      // temporary without printing words on the board (ADR-0033).
      el('span', { class: 'today-acting-bar', 'aria-hidden': 'true' }, idleBar),
      exit,
    );
  }

  function startActing(member: TodayMember, pin?: string): void {
    acting = member;
    actingPin = pin;
    paintBanner();
    resetIdle();
    if (current) paint(current);
  }

  function stopActing(): void {
    clearIdle();
    acting = null;
    actingPin = undefined;
    paintBanner();
    if (current) paint(current);
  }

  // The PIN prompt reuses the shared display's shape (ADR-0049): same sheet,
  // same labels, same "back" escape. Built lazily — a member session never
  // creates it.
  let pinSheet: Sheet | null = null;
  function pinSurface(): Sheet {
    if (!pinSheet) {
      pinSheet = sheet({
        id: 'today-act-as',
        title: t('todayActAsTitle'),
        closeLabel: t('close'),
        variant: 'bottom',
      });
      root.append(pinSheet.root);
    }
    return pinSheet;
  }

  function showPinPrompt(member: TodayMember, message?: string): void {
    const surface = pinSurface();
    clear(surface.body);
    const pin = ce('md-outlined-text-field', {
      id: 'today-pin-input',
      label: t('pickerEnterPin'),
      type: 'password',
      inputmode: 'numeric',
    }) as TextFieldEl;
    const back = ce('md-text-button', { type: 'button' }, t('pickerBack'));
    back.addEventListener('click', () => surface.close());
    surface.body.append(
      el(
        'form',
        {
          onsubmit: (ev: Event) => {
            ev.preventDefault();
            surface.close();
            startActing(member, pin.value);
          },
        },
        el(
          'div',
          { class: 'attribution-pin-head' },
          avatarEl(member.name, member.avatar, member.color, 40),
          el('strong', {}, member.name),
        ),
        el('label', { for: 'today-pin-input' }, t('pickerEnterPin')),
        pin,
        message ? el('p', { class: 'error' }, message) : null,
        el('div', { class: 'row' }, ce('md-filled-button', { type: 'submit' }, t('pickerUnlock')), back),
      ),
    );
    surface.open();
    // `autofocus` does nothing on a custom element; focus once Material has
    // upgraded and rendered its inner input (same as the picker).
    void customElements.whenDefined('md-outlined-text-field').then(() => pin.focus());
  }

  /** Tap on a header cell: enter the mode, PIN first when they have one. */
  function pickMember(member: TodayMember): void {
    error.textContent = '';
    if (member.hasPin) showPinPrompt(member);
    else startActing(member);
  }

  // --- Completion ---------------------------------------------------------

  /** Member session (ADR-0058): the bodyless self-complete. */
  function complete(taskId: number, button: HTMLButtonElement): void {
    // Guard the double-tap: the grid is rebuilt by load() on success, so
    // the button only ever has to survive until then (or until the error).
    if (button.disabled) return;
    button.disabled = true;
    error.textContent = '';
    api
      .post(`/api/tasks/${taskId}/complete`)
      .then(() => {
        load();
        options.onChanged?.();
      })
      .catch((err: Error) => {
        error.textContent = err.message;
        // Reload rather than just re-enabling: a 409 means someone else
        // closed it, and the board should show that truth.
        load();
      });
  }

  /** Shared display (ADR-0061): ADR-0049's on-behalf completion, unchanged —
   *  the server resolves eligibility and enforces the target's PIN. */
  function completeAs(task: TodayTask, member: TodayMember, button: HTMLButtonElement): void {
    if (button.disabled) return;
    button.disabled = true;
    error.textContent = '';
    resetIdle();
    const body: Record<string, unknown> = { asMemberId: member.id };
    if (actingPin) body.pin = actingPin;
    api
      .post(`/api/tasks/${task.id}/complete`, body)
      .then(() => {
        resetIdle();
        load();
        options.onChanged?.();
      })
      .catch((err: Error) => {
        if (err instanceof ApiError && err.status === 403 && err.message === 'wrong PIN') {
          // The PIN was only ever checkable here. Keep the member selected,
          // drop the bad PIN and ask again instead of silently failing.
          actingPin = undefined;
          showPinPrompt(member, t('displayWrongPin'));
          load();
          return;
        }
        error.textContent = err.message;
        load();
      });
  }

  // --- Painting -----------------------------------------------------------

  /** A chip over one contiguous run of columns. Tappable only when `action`
   *  says so; otherwise a plain, inert label. */
  function openChip(task: TodayTask, action: ChipAction | null): HTMLElement {
    const label = el('span', { class: 'today-chip-label' }, task.title);
    if (!action) {
      return el('div', { class: 'today-chip', 'data-today-task': task.title }, label);
    }
    const button = el('button', {
      class: 'today-chip mine',
      type: 'button',
      'data-today-task': task.title,
      'data-today-tappable': '',
      ...action.data,
      // The chip shows the task title; the accessible name says what the
      // tap DOES, so the board stays wordless while the button is not
      // (ADR-0033 + ADR-0036).
      'aria-label': action.label,
      title: action.label,
    });
    button.append(label, icons.done());
    button.addEventListener('click', () => action.run(button));
    return button;
  }

  /** One member column header. On a shared display it is a real control —
   *  tap it to act as that member (ADR-0061). */
  function headCell(member: TodayMember): HTMLElement {
    const parts = [
      avatarEl(member.name, member.avatar, member.color, 28),
      el('div', { class: 'today-name' }, member.name),
      tallyMarks(member.doneToday),
    ];
    if (!display) {
      return el('div', { class: 'today-head', 'data-member-col': member.name }, ...parts);
    }
    const selected = acting !== null && acting.id === member.id;
    const label = selected
      ? t('todayActingStop', { name: member.name })
      : t('todayActAs', { name: member.name });
    const cell = el('button', {
      class: `today-head today-head-pick${selected ? ' acting' : ''}`,
      type: 'button',
      'data-member-col': member.name,
      'data-member-pick': member.name,
      'aria-pressed': selected ? 'true' : 'false',
      'aria-label': label,
      title: label,
    });
    cell.append(...parts);
    // Selected is a fill + border change AND a glyph, never colour alone
    // (ADR-0046).
    if (selected) cell.append(icons.done());
    cell.addEventListener('click', () => (selected ? stopActing() : pickMember(member)));
    return cell;
  }

  function paint(state: TodayState): void {
    clear(grid);
    const colOf = new Map(state.members.map((m, i) => [m.id, i]));
    grid.style.gridTemplateColumns = `repeat(${state.members.length}, minmax(0, 1fr))`;

    const maxMinutes = Math.max(...state.members.map((m) => m.minutesToday), 1);
    state.members.forEach((m, col) => {
      const head = headCell(m);
      head.style.gridColumn = String(col + 1);
      head.style.gridRow = '1';
      // The soft part of the nudge: a faint terracotta wash that grows
      // with today's share of earned minutes. Never labeled.
      const share = m.minutesToday / maxMinutes;
      head.style.background = `color-mix(in srgb, var(--accent) ${Math.round(share * 18)}%, var(--card))`;
      grid.append(head);
    });

    const open = state.tasks.filter((task) => task.status === 'open');
    const done = state.tasks.filter((task) => task.status === 'done');
    empty.style.display = state.tasks.length === 0 ? '' : 'none';

    // Whose column makes a chip tappable right now: your own on a member
    // session (ADR-0058), the selected member's on a shared display
    // (ADR-0061), nobody's when no one is selected there.
    const subject = display ? acting : state.members.find((m) => m.id === state.viewerId) ?? null;
    const subjectCol = subject ? colOf.get(subject.id) : undefined;
    let row = 2;
    for (const task of open) {
      const cols = task.eligibleMemberIds
        .map((id) => colOf.get(id))
        .filter((c): c is number => c !== undefined);
      if (cols.length === 0) continue;
      // An eligibility set can be non-contiguous, so only the run that
      // actually covers the subject's column becomes the button — the other
      // runs of the same task are someone else's.
      const eligible = subject !== null && task.eligibleMemberIds.includes(subject.id);
      for (const [start, end] of runsOf(cols)) {
        const tappable =
          eligible && subjectCol !== undefined && subjectCol >= start && subjectCol <= end;
        const action: ChipAction | null =
          tappable && subject !== null
            ? display
              ? {
                  label: t('todayCompleteFor', { title: task.title, name: subject.name }),
                  run: (button) => completeAs(task, subject, button),
                  data: { 'data-today-for': subject.name },
                }
              : {
                  label: t('todayCompleteOwn', { title: task.title }),
                  run: (button) => complete(task.id, button),
                  data: { 'data-today-mine': '' },
                }
            : null;
        const chip = openChip(task, action);
        chip.style.gridRow = String(row);
        chip.style.gridColumn = `${start + 1} / ${end + 2}`;
        grid.append(chip);
      }
      row++;
    }
    for (const task of done) {
      const col = task.completedBy === null ? undefined : colOf.get(task.completedBy);
      if (col === undefined) continue;
      const chip = el(
        'div',
        { class: 'today-chip done', 'data-today-task': task.title },
        el('span', { class: 'today-chip-label' }, `${task.title} ✓`),
      );
      chip.style.gridRow = String(row);
      chip.style.gridColumn = String(col + 1);
      grid.append(chip);
      row++;
    }
  }

  function load(): void {
    api
      .get<TodayState>('/api/today')
      .then((state) => {
        current = state;
        // A member who was selected and has since gone inactive (or the
        // board simply changed under us) must not stay selected.
        if (acting) {
          const still = state.members.find((m) => m.id === acting!.id);
          if (!still) stopActing();
          else acting = still;
        }
        paint(state);
      })
      .catch((err: Error) => {
        error.textContent = err.message;
      });
  }

  if (display) {
    // Any interaction with the board keeps the selection alive; walking away
    // is what ends it.
    root.addEventListener('pointerdown', () => resetIdle());
    root.addEventListener('keydown', () => resetIdle());
    root.append(banner);
  }
  root.append(error, empty, grid);
  options.bindReload?.(load);
  load();
  return root;
}
